import { Component, HostListener, Inject, NgZone, Optional } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GTM } from 'src/app/services/gtm.service';
import { environment } from 'src/environments/environment';
import { MODAL, ModalService } from './services/modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HasUnsavedChanges, UNSAVED_CHANGES_CHECKERS } from './services/interfaces/has-unsaved-changes.interface';
import { CUSTOMER_EVENT_LOGGED_OUT_ELSEWHERE, CustomerService } from './services/customer.service';
import { ToastService } from './services/toast.service';
import { logError } from './services/helpers/sentry.helper';
import { FeatureSettingsService } from './services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-root',
  templateUrl: './app.component.html',
})
export class AppComponent {
  hideHeader = false;

  @HostListener('window:beforeunload', ['$event'])
  confirmExit(event: BeforeUnloadEvent) {
    if (this.unsavedCheckers?.some(c => c.hasUnsavedChanges())) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('document:keydown.shift.F12', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    this.modalService.display(MODAL.DEBUG_INFOS);
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly modalService: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly customerService: CustomerService,
    public gtm: GTM,
    private readonly toast: ToastService,
    private readonly zone: NgZone,
    private readonly featureSettings: FeatureSettingsService,
    @Optional() @Inject(UNSAVED_CHANGES_CHECKERS) private unsavedCheckers: HasUnsavedChanges[]
  ) {
    this.router.events.subscribe(() => {
      const currentRoute = this.getDeepestChild(this.route);
      this.hideHeader = currentRoute.snapshot.data['hideHeader'] === true;
    });
    this.gtm.init(environment.tracking);
    // Site client en maintenance : on ne touche pas à la session CIAM.
    if (!this.customerMaintenance) {
      this.restoreCiamSession();
      this.followLogoutFromOtherTabs();
    }
    translate.setDefaultLang('en');
    let userLang = navigator.language.toLowerCase();
    if (userLang.indexOf('fr') > -1) {
      translate.use('fr');
    }
  }

  /** Lu par `customerMaintenanceInitializer`, donc connu dès la construction (site client uniquement). */
  private get customerMaintenance(): boolean {
    return !environment.vendor && this.featureSettings.snapshot.customerMaintenance;
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  /**
   * Le jeton HomeByMe n'est plus acquis ici : il l'est à l'ouverture d'un configurateur
   * (`getAuthParams`), sur demande de HomeByMe (`LoginRequired`) ou à l'entrée d'un projet
   * (`CustomerTokenGuard`). Un client qui n'ouvre aucun configurateur n'a pas de compte HBM.
   */
  private restoreCiamSession(): void {
    this.customerService.restoreCiamSession().catch(logError);
  }

  /**
   * Une déconnexion faite dans un autre onglet vaut aussi ici : comme pour une déconnexion
   * locale, le client quitte le projet en cours, qu'il ne pourrait plus enregistrer.
   */
  private followLogoutFromOtherTabs(): void {
    this.customerService.events.subscribe((event: any) => {
      if (event?.type !== CUSTOMER_EVENT_LOGGED_OUT_ELSEWHERE) {
        return;
      }

      // Le message arrive par le BroadcastChannel : on rentre dans la zone pour rafraîchir la vue.
      this.zone.run(async () => {
        this.modalService.close();
        if (this.router.url.split(/[?#]/)[0] !== '/') {
          await this.router.navigateByUrl('/');
        }

        this.toast.showSuccessToast(
          this.translate.instant('popins.logout.success.title'),
          this.translate.instant('popins.logout.elsewhere')
        );
      });
    });
  }

  get enableErrorPanel(): boolean {
    return environment.sentry.dsn?.length <= 0;
  }
}
